import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { normalizePost } from '../../services/postService';
import { Avatar } from '../ui';
import PremiumName from '../ui/PremiumName';
import { useUserData } from '../../hooks/useUserData';
import { useAuth } from '../../hooks/useAuth';
import { Link } from 'react-router-dom';
import { formatPostDate } from '../../utils/dateFormatter';
import { DEFAULT_IMAGES } from '../../constants/defaults';

const FeaturedPostAuthor = ({ post }) => {
  const { userData: currentUserData } = useAuth();
  const isOwnPost = post?.userId === currentUserData?.id;
  const { userData: authorData } = useUserData(isOwnPost ? null : post?.userId);
  const liveData = isOwnPost ? currentUserData : authorData;
  const authorName = post?.authorInfo?.displayName || post?.userName || liveData?.displayName || 'Usuario';
  const isPremium = liveData?.isPremium ?? post?.authorInfo?.isPremium ?? post?.isPremiumAuthor ?? false;
  const premiumColor = liveData?.premiumNameColor ?? post?.authorInfo?.premiumNameColor ?? post?.premiumNameColor ?? null;
  return (
    <PremiumName
      name={authorName}
      isPremium={isPremium}
      premiumColor={premiumColor}
      nameClassName="text-sm font-medium text-gray-900 dark:text-white truncate block"
    />
  );
};

const FeaturedPostAvatar = ({ post }) => {
  const { userData: currentUserData } = useAuth();
  const isOwnPost = post?.userId === currentUserData?.id;
  const { userData: authorData } = useUserData(isOwnPost ? null : post?.userId);
  const liveData = isOwnPost ? currentUserData : authorData;
  const authorName = post?.authorInfo?.displayName || post?.userName || 'Usuario';
  const authorPhotoURL = isOwnPost
    ? (liveData?.photoURL ?? null)
    : (post?.authorInfo?.profileImage || post?.userPhotoURL || liveData?.photoURL || null);
  return (
    <Avatar
      src={authorPhotoURL}
      alt={authorName}
      fallbackText={authorName}
      size="sm"
    />
  );
};

/**
 * FeaturedPosts - Carrusel horizontal de posts destacados
 * Muestra posts con más likes de las últimas 48 horas
 */
const FeaturedPosts = ({ selectedCategory = 'all' }) => {
  const navigate = useNavigate();
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedPosts();
  }, []);

  const loadFeaturedPosts = async () => {
    try {
      const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);
      const postsRef = collection(db, 'posts');
      
      // Query: posts recientes ordenados por likes
      // Nota: requiere index compuesto (createdAt DESC, likesCount DESC)
      const q = query(
        postsRef,
        where('createdAt', '>', twoDaysAgo),
        orderBy('createdAt', 'desc'),
        limit(8)
      );

      const snapshot = await getDocs(q);
      const posts = snapshot.docs
        .map(doc => normalizePost({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0))
        .slice(0, 8);

      setFeaturedPosts(posts);
    } catch (error) {
      console.error('Error al cargar posts destacados:', error);
      // Si falla el index compuesto, cargar posts recientes sin filtro de likes
      try {
        const postsRef = collection(db, 'posts');
        const fallbackQ = query(postsRef, orderBy('createdAt', 'desc'), limit(8));
        const snapshot = await getDocs(fallbackQ);
        const posts = snapshot.docs.map(doc => normalizePost({ id: doc.id, ...doc.data() }));
        setFeaturedPosts(posts);
      } catch (fallbackError) {
        console.error('Error en fallback:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredFeaturedPosts =
    selectedCategory === 'all'
      ? featuredPosts
      : featuredPosts.filter((p) => p?.category === selectedCategory);

  if (loading) {
    return (
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          Posts destacados
        </h2>
        <div className="flex gap-4 overflow-x-auto pb-2 hide-scrollbar">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex-shrink-0 w-64 h-80 bg-gray-200 dark:bg-slate-700 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (filteredFeaturedPosts.length === 0) return null;

  return (
    <div className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Posts destacados
      </h2>
      <div className="flex gap-4 overflow-x-auto pb-2 hide-scrollbar">
        {filteredFeaturedPosts.map((post) => {
          const firstMedia = post.media && post.media.length > 0 ? post.media[0] : null;

          return (
            <div
              key={post.id}
              onClick={() => navigate(`/post/${post.id}`)}
              className="flex-shrink-0 w-64 cursor-pointer group"
            >
              <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 hover:shadow-xl transition-all">
                {firstMedia && (
                  <div className="relative aspect-[4/5] overflow-hidden bg-gray-100 dark:bg-slate-700">
                    {firstMedia.type === 'video' ? (
                      <>
                        <video
                          src={firstMedia.url}
                          poster={firstMedia.thumbnailUrl || DEFAULT_IMAGES.VIDEO_POSTER}
                          muted
                          playsInline
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          preload="metadata"
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                            <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      </>
                    ) : (
                      <img
                        src={firstMedia.url}
                        alt={post.caption || 'Post'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    )}
                    
                    {/* Overlay con stats */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-3 left-3 flex items-center gap-3 text-white text-sm">
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                          </svg>
                          <span className="font-medium">{post.likesCount || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <span className="font-medium">{post.commentsCount || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Info del post */}
                <div className="p-3">
                  <Link 
                    to={`/profile/${post.userId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  >
                    <FeaturedPostAvatar post={post} />
                    <div className="flex-1 min-w-0">
                      <FeaturedPostAuthor post={post} />
                      {post.createdAt && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatPostDate(post.createdAt)}
                        </p>
                      )}
                    </div>
                  </Link>
                  
                  {post.caption && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 line-clamp-2">
                      {post.caption}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FeaturedPosts;
